import { can } from '../utils/permissions';
import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import studentService from '../services/studentService';
import admissionService from '../services/admissionService';
import financeService from '../services/financeService';
import './StudentAnalytics.css';
import PoliceVerificationForm from './PoliceverificationForm';

const PoliceOption = ({ label, icon, color, bg, isCurrent, onClick }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding:'12px 14px', cursor:'pointer',
        display:'flex', alignItems:'center', gap:10,
        background: isCurrent || hover ? bg : '#fff',
        borderLeft: isCurrent ? `3px solid ${color}` : '3px solid transparent',
      }}>
      <span style={{ fontSize:15 }}>{icon}</span>
      <span style={{ fontSize:13, fontWeight:600, color, flex:1 }}>{label}</span>
      {isCurrent && <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:10, background:color, color:'#fff' }}>Current</span>}
    </div>
  );
};

const StudentAnalytics = ({ onNavigate, onLogout, studentId }) => {
  const [student, setStudent] = useState(null);
  const [admission, setAdmission] = useState(null);
  const [feeDetail, setFeeDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPoliceForm, setShowPoliceForm] = useState(false);
  const [policeDropdownOpen, setPoliceDropdownOpen] = useState(false);
  const [policeUpdating, setPoliceUpdating] = useState(false);

  useEffect(() => {
    if (studentId) {
      loadStudentData();
    }
  }, [studentId]);

  const loadStudentData = async () => {
    setLoading(true);
    try {
      const data = await studentService.getStudentById(studentId);
      setStudent(data);
      try {
        const adm = await admissionService.getByStudentId(studentId);
        if (adm) {
          setAdmission(adm);
          const fee = await financeService.getStudentFeeDetail(adm.id).catch(() => null);
          if (fee) setFeeDetail(fee);
        }
      } catch (admErr) {
        console.log('[StudentAnalytics] Error loading admission:', admErr);
      }
    } catch (error) {
      console.error('Error loading student:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const calculateDaysRemaining = () => {
    const till = admission?.validTill || admission?.validityDate;
    if (!till) return 'N/A';
    const diffMs = new Date(till) - new Date();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const getValidTillLabel = () => {
    const till = admission?.validTill || admission?.validityDate;
    return till ? `Valid till ${formatDate(till)}` : 'Validity not set';
  };

  const getTotalFees = () => {
    if (!admission) return null;
    return Number(admission.totalFees) || null;
  };

  const getAmountPaid = () => {
    if (feeDetail?.paidAmount != null) return Number(feeDetail.paidAmount);
    if (!admission) return 0;
    if (admission.paidFees != null) return Number(admission.paidFees);
    if (admission.amountPaid != null) return Number(admission.amountPaid);
    return 0;
  };

  const getBalanceDue = () => {
    const total = getTotalFees();
    if (total === null) return null;
    return total - getAmountPaid();
  };

  const getInstallments = () => {
    if (!admission?.installments) return [];
    let raw = admission.installments;
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch { return []; }
    }
    if (!Array.isArray(raw) || raw.length === 0) return [];
    const total = getTotalFees() || 0;
    const count = raw.length;
    const perSlot   = count > 0 ? Math.floor(total / count) : 0;
    const remainder = total - perSlot * (count - 1);
    return raw.map((item, idx) => {
      const isObj   = item && typeof item === 'object';
      const dateStr = isObj ? (item.date || item.dueDate || '') : item;
      const amount  = isObj && item.amount ? Number(item.amount) : (idx === count - 1 ? remainder : perSlot);
      const dueDate = dateStr ? new Date(dateStr) : null;
      const isPast  = dueDate ? dueDate < new Date() : false;
      const status  = isObj && item.status ? item.status : (isPast ? 'paid' : 'pending');
      return { date: dateStr ? formatDate(dateStr) : `Instalment ${idx + 1}`, amount, status };
    });
  };

  const AMENITY_LABELS = {
    accommodation: 'Accommodation',
    breakfast:     'Breakfast',
    dinner:        'Dinner',
    wifi:          'WiFi',
    readingRoom:   'Reading Room',
    lunch:         'Lunch',
    gymnasium:     'Gymnasium',
  };

  const getRoomDetails = () => {
    if (!admission) return null;
    const bedCode   = admission.bedCode;
    const roomBlock = admission.roomBlock;
    const roomNumber= admission.roomNumber;
    const bedNumber = admission.bedNumber;
    if (!bedCode && !roomBlock) return null;
    let tower = '', wing = '', floorDigit = '', roomDigit = '', bedDigit = '';
    if (bedCode && bedCode.includes('-')) {
      const dash   = bedCode.indexOf('-');
      const prefix = bedCode.substring(0, dash);
      const rest   = bedCode.substring(dash + 1);
      tower      = prefix[0];
      wing       = prefix[1] === 'R' ? 'Rear' : 'Front';
      floorDigit = rest.length >= 3 ? rest.substring(0, rest.length - 2) : '';
      roomDigit  = rest.length >= 2 ? rest.substring(rest.length - 2, rest.length - 1) : rest;
      bedDigit   = rest.substring(rest.length - 1);
    } else {
      tower      = roomBlock ? roomBlock[0] : '';
      wing       = roomBlock && roomBlock[1] === 'R' ? 'Rear' : 'Front';
      const rn   = (roomNumber || '').toString().trim();
      floorDigit = rn.length >= 2 ? rn.charAt(0) : '';
      roomDigit  = rn.length >= 2 ? rn.charAt(rn.length - 1) : rn;
      bedDigit   = (bedNumber || '').toString().trim();
    }
    const floorSuffix = (n) => {
      const num = parseInt(n, 10);
      if (isNaN(num) || num < 1) return '—';
      if (num === 1) return '1st Floor';
      if (num === 2) return '2nd Floor';
      if (num === 3) return '3rd Floor';
      return `${num}th Floor`;
    };
    const displayBedCode = bedCode ||
      (roomBlock && roomNumber && bedNumber ? `${roomBlock}-${roomNumber}${bedNumber}` : '—');
    return {
      bedCode:  displayBedCode,
      tower:    tower ? `Tower ${tower}` : '—',
      wing:     wing || '—',
      floor:    floorSuffix(floorDigit),
      roomNum:  roomDigit || '—',
      bedNum:   bedDigit || '—',
      roomType: (() => {
        const rt = admission.roomType || '';
        const map = {
          '4sharing':'4 Sharing', '4Sharing':'4 Sharing', '4 Sharing':'4 Sharing',
          '3sharing':'3 Sharing', '3Sharing':'3 Sharing', '3 Sharing':'3 Sharing',
          '2sharing':'2 Sharing (Twin)', '2Sharing':'2 Sharing (Twin)', '2 Sharing':'2 Sharing (Twin)',
          '2sharingPremium':'2 Sharing (Premium)',
          'private':'Single / Private', '1sharing':'Single / Private',
        };
        return map[rt] || rt || '—';
      })(),
    };
  };

  const handlePoliceStatusUpdate = async (status) => {
    if (!admission) return;
    setPoliceUpdating(true);
    setPoliceDropdownOpen(false);
    setAdmission(prev => ({ ...prev, policeVerification: status }));
    try {
      await admissionService.updatePoliceVerification(admission.id, status);
    } catch (e) {
      setAdmission(prev => ({ ...prev, policeVerification: admission.policeVerification }));
      console.error('Failed to update police verification:', e);
    } finally {
      setPoliceUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="app-layout">
        <Sidebar activeItem="admission" onNavigate={onNavigate} onLogout={onLogout} />
        <main className="main-content">
          <div style={{ padding: '50px', textAlign: 'center' }}>
            <div style={{ fontSize: '18px', color: '#6B6B6B' }}>Loading student data...</div>
          </div>
        </main>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="app-layout">
        <Sidebar activeItem="admission" onNavigate={onNavigate} onLogout={onLogout} />
        <main className="main-content">
          <div style={{ padding: '50px', textAlign: 'center' }}>
            <div style={{ fontSize: '18px', color: '#E85D4A' }}>Student not found</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar activeItem="admission" onNavigate={onNavigate} onLogout={onLogout} />
      
      <main className="main-content">
        {/* Top Bar */}
        <div className="top-bar">
          <div className="breadcrumb">
            <a href="#" onClick={(e) => { e.preventDefault(); onNavigate('admission'); }}>Admission</a> / <span>Student Analytics</span>
          </div>
          <h1 className="page-title">Student Analytics Dashboard</h1>
        </div>

        {/* Student Header */}
        <div className="analytics-student-header">
          {student.photoUrl ? (
            <img
              src={studentService.getPhotoUrl(student.id)}
              alt="Student"
              className="analytics-header-photo"
              onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
            />
          ) : null}
          <div className="analytics-header-photo" style={{display: student.photoUrl ? 'none' : 'flex', alignItems:'center', justifyContent:'center', background:'#2A4A6B', fontSize:40, color:'white'}}>👤</div>
          <div className="analytics-header-info">
            <div className="analytics-header-name">{student.fullName || 'Student Name'}</div>
            <div className="analytics-header-meta">
              <div className="analytics-header-meta-item">
                <span>🆔</span>
                <span>Student ID: {admission?.admissionNumber || student.registrationId || 'N/A'}</span>
              </div>
              <div className="analytics-header-meta-item">
                <span>📍</span>
                <span>Room: {admission?.bedCode || admission?.roomBlock
                  ? `${admission.bedCode || admission.roomBlock + '-' + admission.roomNumber}`
                  : 'Not Allocated'}</span>
              </div>
              <div className="analytics-header-meta-item">
                <span>📅</span>
                <span>Admitted: {formatDate(student.createdAt)}</span>
              </div>
              <div className="analytics-header-meta-item">
                <span>📞</span>
                <span>{student.mobile || 'N/A'}</span>
              </div>
            </div>
          </div>
          {(() => {
            const d = calculateDaysRemaining();
            if (admission && typeof d === 'number' && d <= 30) {
              return (
                <div style={{ marginLeft: 'auto', alignSelf: 'flex-start' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 16px', borderRadius: 30, fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', background: 'rgba(255,152,0,0.16)', color: '#FFB74D', border: '1px solid rgba(255,152,0,0.45)' }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#FFB74D' }}></span>
                    {d <= 0 ? 'Expired' : 'Expiring Soon'}
                  </span>
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* KPI Cards */}
        <div className="analytics-kpi-grid">
          <div className="analytics-kpi-card fees">
            <div className="analytics-kpi-icon">💰</div>
            <div className="analytics-kpi-label">Total Fees</div>
            <div className="analytics-kpi-value">
              {getTotalFees() !== null ? `₹${getTotalFees().toLocaleString()}` : '—'}
            </div>
            <div className="analytics-kpi-subtext">
              {admission ? 'As per admission record' : 'Admission not confirmed'}
            </div>
          </div>
          <div className="analytics-kpi-card balance">
            <div className="analytics-kpi-icon">⚠️</div>
            <div className="analytics-kpi-label">Balance Due</div>
            <div className="analytics-kpi-value">
              {getBalanceDue() !== null ? `₹${getBalanceDue().toLocaleString()}` : '—'}
            </div>
            <div className="analytics-kpi-subtext">
              {getAmountPaid() > 0 ? `₹${getAmountPaid().toLocaleString()} paid` : 'No payments recorded yet'}
            </div>
          </div>
          <div className="analytics-kpi-card verification">
            <div className="analytics-kpi-icon">🛡️</div>
            <div className="analytics-kpi-label">Police Verification</div>
            <div className="analytics-kpi-value" style={{ color: (admission?.policeVerification || 'Pending') === 'Completed' ? '#2D5F4F' : '#E85D4A' }}>
              {policeUpdating ? 'Updating…' : (admission?.policeVerification || 'Pending')}
            </div>
            <div className="analytics-kpi-subtext">
              {(admission?.policeVerification || 'Pending') === 'Completed' ? '✓ Verification done' : 'Verification pending'}
            </div>
          </div>
          <div className="analytics-kpi-card validity">
            <div className="analytics-kpi-icon">📆</div>
            <div className="analytics-kpi-label">Days Remaining</div>
            <div className="analytics-kpi-value">{calculateDaysRemaining()}</div>
            <div className="analytics-kpi-subtext">{getValidTillLabel()}</div>
            {/* {admission && (
              <div
                onClick={() => onNavigate('studentTenure', { studentId })}
                style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #E8E4DF', color: '#D4AF37', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Manage Tenure →
              </div>
            )} */}

            {admission && (
  <div
    title="Coming soon"
    style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #E8E4DF', color: '#C4BEB8', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', cursor: 'not-allowed', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    Manage Tenure →
  </div>
)}
          </div>
        </div>

        {/* Content Grid */}
        <div className="content-area">
          {/* Row 1: Fee Breakdown + Payment Schedule */}
          <div className="analytics-content-grid">
            <div className="analytics-card">
              <div className="analytics-card-header">
                <span>💳</span>
                <span>Fee Breakdown</span>
              </div>
              <div className="analytics-card-body">
                {!admission ? (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: '#C4BEB8', fontSize: 14 }}>Admission not confirmed yet</div>
                ) : (
                  <div className="analytics-fee-breakdown">
                    {admission.roomType && (
                      <div className="analytics-fee-item">
                        <span className="analytics-fee-item-label">Room Type</span>
                        <span className="analytics-fee-item-amount" style={{ fontSize: 13 }}>
                          {{'4sharing':'4 Sharing','2sharing':'2 Sharing (Twin)','2sharingPremium':'2 Sharing (Premium)','private':'Single / Private'}[admission.roomType] || admission.roomType}
                        </span>
                      </div>
                    )}
                    {admission.months > 0 && (
                      <div className="analytics-fee-item">
                        <span className="analytics-fee-item-label">Months Billed</span>
                        <span className="analytics-fee-item-amount" style={{ fontSize: 13 }}>{admission.months} {admission.months === 1 ? 'month' : 'months'}</span>
                      </div>
                    )}
                    <div className="analytics-fee-item">
                      <span className="analytics-fee-item-label">Accommodation Charges</span>
                      <span className="analytics-fee-item-amount">₹{Number(admission.hostelFees||0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="analytics-fee-item">
                      <span className="analytics-fee-item-label">Food Charges</span>
                      <span className="analytics-fee-item-amount">₹{Number(admission.foodCharges||0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="analytics-fee-item">
                      <span className="analytics-fee-item-label">Maintenance Fix Charges</span>
                      <span className="analytics-fee-item-amount">₹{Number(admission.maintenanceCharges||0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="analytics-fee-item">
                      <span className="analytics-fee-item-label">Security Deposit</span>
                      <span className="analytics-fee-item-amount">₹{Number(admission.refundableDeposit||0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="analytics-fee-item">
                      <span className="analytics-fee-item-label">Prepaid Card</span>
                      <span className="analytics-fee-item-amount">₹{Number(admission.prepaidCard||0).toLocaleString('en-IN')}</span>
                    </div>

{(admission.mattressCharge > 0) && (
                      <div className="analytics-fee-item">
                        <span className="analytics-fee-item-label">Two-Step Payment Premium</span>
                        <span className="analytics-fee-item-amount">₹{Number(admission.mattressCharge).toLocaleString('en-IN')}</span>
                      </div>
                    )}

                    {(admission.gst > 0) && (
                      <div className="analytics-fee-item">
                        <span className="analytics-fee-item-label">GST</span>
                        <span className="analytics-fee-item-amount">₹{Number(admission.gst).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <div className="analytics-fee-item">
                      <span className="analytics-fee-item-label">Payment Mode</span>
                      <span className="analytics-fee-item-amount" style={{ fontSize: 13 }}>{admission.paymentMode || '—'}</span>
                    </div>
                    {admission.transactionId && (
                      <div className="analytics-fee-item">
                        <span className="analytics-fee-item-label">Transaction ID</span>
                        <span className="analytics-fee-item-amount" style={{ fontSize: 12, fontFamily: 'monospace' }}>{admission.transactionId}</span>
                      </div>
                    )}
                    <div className="analytics-fee-item">
                      <span className="analytics-fee-item-label">Date of Admission</span>
                      <span className="analytics-fee-item-amount" style={{ fontSize: 13 }}>{formatDate(admission.dateOfAdmission)}</span>
                    </div>
                    <div className="analytics-fee-item">
                      <span className="analytics-fee-item-label">Valid Till</span>
                      <span className="analytics-fee-item-amount" style={{ fontSize: 13 }}>{formatDate(admission.validTill || admission.validityDate)}</span>
                    </div>
                    <div className="analytics-fee-item" style={{ borderTop: '2px solid #E8E4DF', marginTop: 4, paddingTop: 10 }}>
                      <span className="analytics-fee-item-label" style={{ fontWeight: 700, color: '#1B3B5F' }}>Total Payable</span>
                      <span className="analytics-fee-item-amount" style={{ fontWeight: 700, color: '#1B3B5F' }}>
                        {getTotalFees() !== null ? `₹${getTotalFees().toLocaleString('en-IN')}` : '—'}
                      </span>
                    </div>
                    <div className="analytics-fee-item paid">
                      <span className="analytics-fee-item-label">Amount Paid</span>
                      <span className="analytics-fee-item-amount">₹{getAmountPaid().toLocaleString('en-IN')}</span>
                    </div>
                    <div className="analytics-fee-item pending">
                      <span className="analytics-fee-item-label">Balance Outstanding</span>
                      <span className="analytics-fee-item-amount">
                        {getBalanceDue() !== null ? `₹${getBalanceDue().toLocaleString('en-IN')}` : '—'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Schedule */}
            <div className="analytics-card">
              <div className="analytics-card-header">
                <span>📅</span>
                <span>Payment Schedule</span>
              </div>
              <div className="analytics-card-body">
                {!admission ? (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: '#C4BEB8', fontSize: 14 }}>Admission not confirmed yet</div>
                ) : getInstallments().length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: '#C4BEB8', fontSize: 14 }}>No installment schedule set</div>
                ) : (
                  <div className="analytics-timeline">
                    {getInstallments().map((installment, index) => (
                      <div key={index} className="analytics-timeline-item">
                        <div className={`analytics-timeline-dot ${installment.status === 'paid' ? 'completed' : ''}`}></div>
                        <div className="analytics-timeline-date">{installment.date}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Package Inclusions + Attendance */}
          <div className="analytics-content-grid" style={{ marginTop: '30px' }}>
            <div className="analytics-card">
              <div className="analytics-card-header">
                <span>📦</span>
                <span>Package Inclusions</span>
              </div>
              <div className="analytics-card-body">
                {!admission ? (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: '#C4BEB8', fontSize: 14 }}>Admission not confirmed yet</div>
                ) : !admission.amenities ? (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: '#C4BEB8', fontSize: 14 }}>No amenities recorded</div>
                ) : (
                  <div className="analytics-package-grid">
                    {Object.entries(AMENITY_LABELS).map(([key, label]) => {
                      const isIncluded = admission.amenities[key] === true;
                      return (
                        <div key={key} className={`analytics-package-item ${isIncluded ? 'included' : 'excluded'}`}>
                          <span className={`amenity-icon ${isIncluded ? 'amenity-yes' : 'amenity-no'}`}>{isIncluded ? '✓' : '✗'}</span>
                          <span>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-card-header">
                <span>📊</span>
                <span>Attendance Overview</span>
                <span style={{ marginLeft: 'auto', fontSize: '18px', fontWeight: '700', color: 'rgba(255,255,255,0.9)' }}>89%</span>
              </div>
              <div className="analytics-card-body">
                <div className="analytics-chart-placeholder">
                  <div className="analytics-chart-icon">📈</div>
                  <div className="analytics-chart-text">Attendance trend chart will be displayed here</div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Room Details + Emergency Contacts */}
          <div className="analytics-content-grid" style={{ marginTop: '30px' }}>
            <div className="analytics-card">
              <div className="analytics-card-header">
                <span>🏠</span>
                <span>Room Details</span>
              </div>
              <div className="analytics-card-body">
                {!getRoomDetails() ? (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: '#C4BEB8', fontSize: 14 }}>No bed allocated yet</div>
                ) : (
                  <div className="analytics-room-info">
                    <div className="analytics-room-detail">
                      <div className="analytics-room-detail-label">Bed Code</div>
                      <div className="analytics-room-detail-value" style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1B3B5F' }}>{getRoomDetails().bedCode}</div>
                    </div>
                    <div className="analytics-room-detail">
                      <div className="analytics-room-detail-label">Tower</div>
                      <div className="analytics-room-detail-value">{getRoomDetails().tower}</div>
                    </div>
                    <div className="analytics-room-detail">
                      <div className="analytics-room-detail-label">Wing</div>
                      <div className="analytics-room-detail-value">{getRoomDetails().wing}</div>
                    </div>
                    <div className="analytics-room-detail">
                      <div className="analytics-room-detail-label">Floor</div>
                      <div className="analytics-room-detail-value">{getRoomDetails().floor}</div>
                    </div>
                    <div className="analytics-room-detail">
                      <div className="analytics-room-detail-label">Room Number</div>
                      <div className="analytics-room-detail-value">{getRoomDetails().roomNum}</div>
                    </div>
                    <div className="analytics-room-detail">
                      <div className="analytics-room-detail-label">Bed Number</div>
                      <div className="analytics-room-detail-value">Bed {getRoomDetails().bedNum}</div>
                    </div>
                    <div className="analytics-room-detail">
                      <div className="analytics-room-detail-label">Room Type</div>
                      <div className="analytics-room-detail-value">{getRoomDetails().roomType}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-card-header">
                <span>👥</span>
                <span>Emergency Contacts</span>
              </div>
              <div className="analytics-card-body">
                <div className="analytics-contacts-grid">
                  <div className="analytics-contact-card">
                    <div className="analytics-contact-label">Father</div>
                    <div className="analytics-contact-value">{student.fatherName || 'Not provided'}</div>
                    <div className="analytics-contact-label">Phone</div>
                    <div className="analytics-contact-value">{student.fatherMobile || 'Not provided'}</div>
                    <div className="analytics-contact-label">Email</div>
                    <div className="analytics-contact-value">{student.fatherEmail || 'Not provided'}</div>
                  </div>
                  <div className="analytics-contact-card secondary">
                    <div className="analytics-contact-label">Mother</div>
                    <div className="analytics-contact-value">{student.motherName || 'Not provided'}</div>
                    <div className="analytics-contact-label">Phone</div>
                    <div className="analytics-contact-value">{student.motherMobile || 'Not provided'}</div>
                    <div className="analytics-contact-label">Email</div>
                    <div className="analytics-contact-value">{student.motherEmail || 'Not provided'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Action Bar — Edit Admission button REMOVED */}
        <div className="analytics-action-bar">
          <button className="analytics-btn analytics-btn-navy" onClick={() => setShowDetailsModal(true)}>
            <span>📄</span>
            <span>View All Details</span>
          </button>
          <div style={{ position:'relative', display:'inline-block' }}>
            <button className="analytics-btn analytics-btn-orange"
              onClick={() => setPoliceDropdownOpen(o => !o)}
              disabled={policeUpdating}
              style={{ textTransform:'none', minWidth:'auto', letterSpacing:'normal' }}>
              <span>{(admission?.policeVerification || 'Pending') === 'Completed' ? '✅' : '⏳'}</span>
              <span>Police Verification</span>
              <span style={{ fontSize:10, opacity:0.7, marginLeft:2 }}>▼</span>
            </button>
            {policeDropdownOpen && (
              <div style={{
                position:'absolute', bottom:'calc(100% + 6px)', left:0, zIndex:999,
                background:'#fff', borderRadius:10, boxShadow:'0 8px 32px rgba(0,0,0,0.18)',
                border:'1px solid #E8E4DF', minWidth:220, overflow:'hidden'
              }}>
                <PoliceOption label="Mark as Completed" icon="✅" color="#2D5F4F" bg="#E8F5F0"
                  isCurrent={(admission?.policeVerification || 'Pending') === 'Completed'}
                  onClick={() => { handlePoliceStatusUpdate('Completed'); }} />
                <PoliceOption label="Mark as Pending" icon="⏳" color="#E85D4A" bg="#FFF0EE"
                  isCurrent={(admission?.policeVerification || 'Pending') === 'Pending'}
                  onClick={() => { handlePoliceStatusUpdate('Pending'); }} />
              </div>
            )}
          </div>
          <button className="analytics-btn analytics-btn-navy"
            onClick={() => setShowPoliceForm(true)}
            style={{ textTransform:'none', minWidth:'auto', letterSpacing:'normal' }}>
            <span>📄</span>
            <span>Police Verification Form</span>
          </button>
        </div>

        {/* Police Verification Form Modal */}
        {showPoliceForm && (
          <PoliceVerificationForm student={student} onClose={() => setShowPoliceForm(false)} />
        )}

        {/* Details Modal */}
        {showDetailsModal && (
          <div className="modal-overlay active" onClick={() => setShowDetailsModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-close-bar">
                <div>
                  <div className="modal-title">Complete Student Information</div>
                  <div className="student-id-badge-detail">Student ID: {admission?.admissionNumber || student.registrationId}</div>
                </div>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <button onClick={() => { window.print(); }}
                    style={{ padding:'8px 18px', background:'#1B3B5F', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
                    <span>⬇️</span><span>Download Student Details</span>
                  </button>
                  <button className="modal-close-btn" onClick={() => setShowDetailsModal(false)}>✕ Close</button>
                </div>
              </div>
              <div className="modal-content-wrapper">
                <div style={{ background:'#fff', borderRadius:12, padding:'28px 32px', marginBottom:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid #F0EDE8' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:28, marginBottom:24 }}>
                    <div style={{ width:100, height:100, borderRadius:'50%', border:'3px solid #C9A227', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', background:'#F4F1EC', fontSize:44 }}>
                      {student.photoUrl ? (
                        <img src={studentService.getPhotoUrl(student.id)} alt="Student"
                          style={{ width:'100%', height:'100%', objectFit:'cover' }}
                          onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='inline'; }} />
                      ) : null}
                      <span style={{ fontSize:44, display: student.photoUrl ? 'none' : 'inline' }}>👤</span>
                    </div>
                    <div>
                      <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:32, color:'#1B3B5F', fontWeight:500, marginBottom:4 }}>{student.fullName}</div>
                      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                        <span style={{ fontSize:12, fontWeight:700, padding:'3px 12px', borderRadius:20, background:'#1B3B5F20', color:'#1B3B5F' }}>
                          Student ID: {admission?.admissionNumber || student.registrationId || '—'}
                        </span>
                        {admission?.bedCode && (
                          <span style={{ fontSize:12, fontWeight:700, padding:'3px 12px', borderRadius:20, background:'#C9A22720', color:'#8B6914' }}>
                            🛏 {admission.bedCode}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'16px 24px', borderTop:'1px solid #F0EDE8', paddingTop:20 }}>
                    {[
                      { label:'Date of Birth',   value: formatDate(student.dob) },
                      { label:'Gender',           value: student.gender },
                      { label:'Room Number',      value: admission?.bedCode || admission?.roomNumber || 'Not Allocated' },
                      { label:'Mobile Number',    value: student.mobile },
                      { label:'Email Address',    value: student.email },
                      { label:'Admission Date',   value: formatDate(admission?.dateOfAdmission || student.createdAt) },
                    ].map(f => (
                      <div key={f.label}>
                        <div style={{ fontSize:11, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:'#6B6B6B', marginBottom:5 }}>{f.label}</div>
                        <div style={{ fontSize:14, fontWeight:600, color:'#1B3B5F' }}>{f.value || '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="section-card">
                  <div className="section-header-detail"><div className="section-icon-detail">👤</div><div className="section-title-detail"><h3>Personal Information</h3><p>Basic personal details and identification</p></div></div>
                  <div className="fields-grid-detail">
                    {[['Full Name',student.fullName],['Date of Birth',formatDate(student.dob)],['Gender',student.gender],['Marital Status',student.maritalStatus],['Religion',student.religion],['Blood Group',student.bloodGroup],['Native Region',student.nativeRegion],['Mobile Number',student.mobile],['Email Address',student.email]].map(([label,value])=>(
                      <div key={label} className="field-group-detail"><div className="field-label-detail">{label}</div><div className="field-value-detail">{value||'Not provided'}</div></div>
                    ))}
                  </div>
                </div>

                <div className="section-card">
                  <div className="section-header-detail"><div className="section-icon-detail">👨‍👩‍👦</div><div className="section-title-detail"><h3>Family Details</h3><p>Parent and family contact information</p></div></div>
                  <div className="fields-grid-detail">
                    {[["Father's Name",student.fatherName],["Father's Mobile",student.fatherMobile],["Father's Email",student.fatherEmail],["Mother's Name",student.motherName],["Mother's Mobile",student.motherMobile],["Mother's Email",student.motherEmail]].map(([label,value])=>(
                      <div key={label} className="field-group-detail"><div className="field-label-detail">{label}</div><div className="field-value-detail">{value||'Not provided'}</div></div>
                    ))}
                  </div>
                </div>

                <div className="section-card">
                  <div className="section-header-detail"><div className="section-icon-detail">📍</div><div className="section-title-detail"><h3>Address Information</h3><p>Permanent and correspondence addresses</p></div></div>
                  <div className="address-columns-detail">
                    <div className="address-column-detail">
                      <h4>Permanent Address</h4>
                      <div className="address-fields-detail">
                        {[['Address Line 1',student.permanentAddress?.address1],['Address Line 2',student.permanentAddress?.address2],['City',student.permanentAddress?.city],['State',student.permanentAddress?.state],['Zip Code',student.permanentAddress?.zipcode],['Country',student.permanentAddress?.country]].map(([label,value])=>(
                          <div key={label} className="field-group-detail"><div className="field-label-detail">{label}</div><div className="field-value-detail">{value||'Not provided'}</div></div>
                        ))}
                      </div>
                    </div>
                    <div className="address-column-detail">
                      <h4>Correspondence Address</h4>
                      <div className="address-fields-detail">
                        {[['Address Line 1',student.correspondenceAddress?.address1],['Address Line 2',student.correspondenceAddress?.address2],['City',student.correspondenceAddress?.city],['State',student.correspondenceAddress?.state],['Zip Code',student.correspondenceAddress?.zipcode],['Country',student.correspondenceAddress?.country]].map(([label,value])=>(
                          <div key={label} className="field-group-detail"><div className="field-label-detail">{label}</div><div className="field-value-detail">{value||'Same as permanent'}</div></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="section-card">
                  <div className="section-header-detail"><div className="section-icon-detail">👤</div><div className="section-title-detail"><h3>Local Guardian Details</h3><p>Emergency contact person in the city</p></div></div>
                  <div className="fields-grid-detail">
                    {[["Guardian's Name",student.localGuardianName],['Relation',student.guardianRelation],["Guardian's Mobile",student.guardianMobile],["Guardian's Email",student.guardianEmail]].map(([label,value])=>(
                      <div key={label} className="field-group-detail"><div className="field-label-detail">{label}</div><div className="field-value-detail">{value||'Not provided'}</div></div>
                    ))}
                  </div>
                </div>

                <div className="section-card">
                  <div className="section-header-detail"><div className="section-icon-detail">🆔</div><div className="section-title-detail"><h3>ID Proof Details</h3><p>Government identification documents</p></div></div>
                  <div className="fields-grid-detail">
                    {[['Aadhar Card Number',student.aadhar],['Emergency Contact',student.emergencyContact],['Driving Licence Number',student.drivingLicense],['Passport Number',student.passport]].map(([label,value])=>(
                      <div key={label} className="field-group-detail"><div className="field-label-detail">{label}</div><div className="field-value-detail">{value||'Not provided'}</div></div>
                    ))}
                  </div>
                </div>

                {student.courses && student.courses.length > 0 && (
                  <div className="section-card">
                    <div className="section-header-detail"><div className="section-icon-detail">🎓</div><div className="section-title-detail"><h3>Academic Information</h3><p>University and course details</p></div></div>
                    <table className="data-table-detail">
                      <thead><tr><th>University Name</th><th>Academic Year</th><th>Stream</th><th>Timing</th><th>University Phone</th></tr></thead>
                      <tbody>
                        {student.courses.map((course, index) => (
                          <tr key={index}><td>{course.universityName||'N/A'}</td><td>{course.academicYear||'N/A'}</td><td>{course.stream||'N/A'}</td><td>{course.timing||'N/A'}</td><td>{course.phoneNo||'N/A'}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="section-card">
                  <div className="section-header-detail"><div className="section-icon-detail">🗣️</div><div className="section-title-detail"><h3>Language Proficiency</h3><p>Communication skills and language abilities</p></div></div>
                  <table className="data-table-detail">
                    <thead><tr><th>To Write</th><th>To Speak</th><th>To Read</th><th>Mother Tongue</th></tr></thead>
                    <tbody><tr><td>{student.languagesToWrite||'Not provided'}</td><td>{student.languagesToSpeak||'Not provided'}</td><td>{student.languagesToRead||'Not provided'}</td><td>{student.motherTongue||'Not provided'}</td></tr></tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default StudentAnalytics;